<template>
  <div
    class="
      accordion accordion-flush
      card
      shadow-sm
      rounded
      overflow-hidden
      position-absolute
      bottom-0
      end-0
    "
    id="total-station-output"
  >
    <div class="accordion-item">
      <button
        class="card-header h6 fw-bold accordion-button bg-secondary text-light"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#total-station-output-collapse"
        aria-expanded="true"
        aria-controls="instructions-collapse"
      >
        <div class="accordion-header" id="total-station-output-label">
          Total Station Data Output
        </div>
      </button>
      <div
        id="total-station-output-collapse"
        class="accordion-collapse collapse show"
        aria-labelledby="total-station-output-label"
        data-bs-parent="#total-station-output"
      >
        <div class="accordion-body p-0">
          <div class="p-0">
            <div class="table-responsive">
              <table
                id="data-output"
                class="
                  table table-sm table-striped table-borderless
                  m-0
                  overflow-hidden
                "
                aria-label="Total Station Data Output Table"
              >
                <!-- Invisible header for screen readers -->
                <thead class="d-none">
                  <tr>
                    <th scope="col">Attribute</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
                <tbody class="text-start">
                  <tr>
                    <td class="px-3">Hz</td>
                    <td>
                      {{ minuteNotation(HzConverted) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="px-3">V</td>
                    <td>{{ minuteNotation(VConverted) }}</td>
                  </tr>
                  <tr>
                    <td class="px-3">Horiz. Distance</td>
                    <td>{{ formatFloat(horizontalDistance) }} ft</td>
                  </tr>
                  <tr>
                    <td class="px-3">Height Difference</td>
                    <td>{{ formatFloat(heightDifference) }} ft</td>
                  </tr>
                  <tr>
                    <td class="px-3">Target Height</td>
                    <td>{{ targetHeight }} ft</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { metersToFeet, degreesToRadians } from "@/helpers/surveyingUtils.js";
import { DISPLAYED_TARGET_HEIGHT } from "@/helpers/constants.js";

import * as THREE from "three";

export default {
  name: "DataOutput",
  props: {
    hz: Number,
    intersectHypotenuse: Number, // in meters
    V: Number,
    isInverted: Boolean
  },
  data() {
    return {
      targetHeight: DISPLAYED_TARGET_HEIGHT,
      heightInputActive: true,
      heightDifference: null,
      horizontalDistance: null
    };
  },
  computed: {
    ...mapGetters(["getInstrumentHeight"]),
    totalStationLensHeight: {
      get() {
        return this.getInstrumentHeight;
      },
      set(value) {
        this.updateInstrumentHeight(Number(value));
      }
    },
    VConverted() {
      // convert angle ranges used by
      // camera controls to ranges used in surveying
      const bottomLimit = this.isInverted ? 360 : 180;
      const topLimit = this.isInverted ? 180 : 0;
      return THREE.MathUtils.mapLinear(this.V, 0, 180, bottomLimit, topLimit);
    },
    HzConverted() {
      // Flip hz based on inversion of total station
      const rawAngle = this.isInverted ? this.hz - 180 : this.hz;
      // angle increases as camera rotates clockwise
      const azimuthAngleDegrees = -1 * (rawAngle % 360);
      // Angle cannot be negative
      return azimuthAngleDegrees >= 0
        ? azimuthAngleDegrees
        : 360 + azimuthAngleDegrees;
    }
  },
  methods: {
    ...mapActions(["updateInstrumentHeight"]),
    formatFloat(num) {
      return num ? num.toFixed(2) : "";
    },
    roundToSingleDecimalPoint(num) {
      return Math.round(num * 10) / 10;
    },
    minuteNotation(angle) {
      const pad = (num, digits = 2, showFloat = false) => {
        // if showFloat is true, make sure to always show one decimal point (.0 if necessary)
        const numString = showFloat ? num.toFixed(1) : num.toString();
        return numString.padStart(digits, "0");
      };
      // pad degrees to 3 digits, since 360 degrees is the maximum angle
      const degrees = pad(Math.trunc(angle), 3);
      // pad minutes to 2 digits
      const minutes = pad(Math.trunc((angle % 1) * 60));
      // compute seconds, round to single decimal point, then pad to 2 digits
      const seconds = pad(
        this.roundToSingleDecimalPoint(((angle % 1) - minutes / 60) * 3600),
        4,
        true
      );
      return `${degrees}\xB0 ${minutes}' ${seconds}"`;
    },
    hideHeightInput() {
      this.heightInputActive = false;
    },
    updateHorizontalDistance() {
      // Compute horizontal distance in feet
      const a = Math.sin(degreesToRadians(this.V)) * this.intersectHypotenuse;
      this.horizontalDistance =
        this.intersectHypotenuse > -1 ? metersToFeet(a) : null;
    },
    updateHeightDifference() {
      // Compute vertical distance in feet
      const b =
        Number(this.totalStationLensHeight) +
        this.horizontalDistance * Math.cos(degreesToRadians(this.VConverted)) -
        this.targetHeight;

      this.heightDifference = this.intersectHypotenuse > -1 ? b : null;
    }
  },
  watch: {
    intersectHypotenuse() {
      this.updateHorizontalDistance();
      this.updateHeightDifference();
    }
  }
};
</script>

<style scoped>
tr {
  padding: 0px 10px !important;
}
.accordion {
  min-width: 40%;
}

/* Animation styles for height input row */
.fade-row-enter-active,
.fade-row-leave-active {
  transition: all 0.3s ease;
}

.fade-row-enter-from,
.fade-row-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.fade-row-enter-to,
.fade-row-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
